import {useEffect, useState} from "react";
import {Link, useParams} from "react-router";
import {ShieldCheck, UserRound} from "lucide-react";
import {getAdminUser, searchAdminUsers} from "@/api/AdminUserApi.ts";
import {AdminUser} from "@/admin/users/AdminUser.ts";
import SearchBar from "@/search/SearchBar.tsx";
import {Button} from "@/components/ui/button.tsx";

export default function AdminUsersView() {
    const {userId} = useParams();
    const [query, setQuery] = useState("");
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [selected, setSelected] = useState<AdminUser>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalUsers, setTotalUsers] = useState(0);

    useEffect(() => {
        let cancelled = false;
        searchAdminUsers({page, size: 50, sort: "username"}, query || undefined)
            .then(response => {
                if (!cancelled) {
                    setUsers(response.content);
                    setTotalPages(response.page.totalPages);
                    setTotalUsers(response.page.totalElements);
                    setError(false);
                }
            })
            .catch(() => { if (!cancelled) setError(true); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [page, query]);

    useEffect(() => {
        let cancelled = false;
        if (!userId) return;
        getAdminUser(userId).then(user => {
            if (!cancelled) setSelected(user);
        }).catch(() => {
            if (!cancelled) setSelected(undefined);
        });
        return () => { cancelled = true; };
    }, [userId]);

    const activeSelected = selected?.id === userId ? selected : undefined;

    return <div className="flex h-full min-h-0">
        <aside className="flex w-80 shrink-0 flex-col border-r border-border bg-surface">
            <div className="space-y-2 border-b border-border p-3">
                <h1 className="text-lg font-semibold">Benutzer</h1>
                <SearchBar value={query} onChange={value => {
                    setLoading(true);
                    setPage(0);
                    setQuery(value);
                }}/>
            </div>
            <nav className="grow overflow-auto" aria-label="Benutzer">
                {loading && <p role="status" className="p-3 text-sm">Benutzer werden geladen…</p>}
                {error && <p role="alert" className="p-3 text-sm text-red-400">Benutzer konnten nicht geladen werden.</p>}
                {!loading && !error && users.length === 0 && <p className="p-3 text-sm text-muted-foreground">Keine Benutzer gefunden.</p>}
                {users.map(user => <Link key={user.id} to={`/admin/users/${user.id}`}
                    className={`flex items-center gap-3 border-b border-slate-700 px-4 py-3 hover:bg-white/10 ${user.id === userId ? "bg-fuchsia-950" : ""}`}>
                    <UserAvatar user={user}/>
                    <span className="min-w-0 grow">
                        <span className="flex items-center gap-1 truncate font-medium">{user.displayName || user.username}{user.admin && <ShieldCheck className="size-3 text-fuchsia-400"/>}</span>
                        <span className="block truncate text-xs text-muted-foreground">{user.email || user.username}</span>
                    </span>
                </Link>)}
            </nav>
            <div className="flex items-center gap-2 border-t border-border p-3 text-sm text-muted-foreground">
                <span className="grow">{totalUsers} Benutzer</span>
                <Button type="button" size="sm" variant="outline" disabled={loading || page === 0}
                        onClick={() => { setLoading(true); setPage(value => value - 1); }}>Zurück</Button>
                <span>{totalPages === 0 ? 0 : page + 1}/{totalPages}</span>
                <Button type="button" size="sm" variant="outline" disabled={loading || page + 1 >= totalPages}
                        onClick={() => { setLoading(true); setPage(value => value + 1); }}>Weiter</Button>
            </div>
        </aside>
        <main className="min-w-0 grow overflow-auto bg-background p-6">
            <title>{activeSelected ? `${activeSelected.displayName || activeSelected.username} | Benutzer` : "Benutzerverwaltung | DireDawn"}</title>
            {!userId && <div className="grid h-full place-content-center text-muted-foreground">Wähle einen Benutzer aus.</div>}
            {userId && !activeSelected && <p role="status">Benutzer wird geladen…</p>}
            {activeSelected && <div className="mx-auto max-w-4xl space-y-6">
                <div className="flex items-center gap-4">
                    <UserAvatar user={activeSelected} large/>
                    <div>
                        <h2 className="flex items-center gap-2 text-2xl font-semibold">{activeSelected.displayName || activeSelected.username}{activeSelected.admin && <ShieldCheck className="size-5 text-fuchsia-400"/>}</h2>
                        <p className="text-muted-foreground">{activeSelected.email || activeSelected.username}</p>
                    </div>
                </div>
                <dl className="grid gap-3 rounded-lg border bg-card p-4 sm:grid-cols-2">
                    <div><dt className="text-xs text-muted-foreground">Benutzername</dt><dd>{activeSelected.username}</dd></div>
                    <div><dt className="text-xs text-muted-foreground">Letzter Login</dt><dd>{formatLogin(activeSelected.lastLoginAt)}</dd></div>
                </dl>
                <section className="space-y-3">
                    <h3 className="text-lg font-semibold">Kampagnen</h3>
                    {activeSelected.campaigns.length === 0 && <p className="text-muted-foreground">Keine Kampagnen zugeordnet.</p>}
                    <div className="grid gap-2 sm:grid-cols-2">
                        {activeSelected.campaigns.map(campaign => <Link key={campaign.campaignId}
                            to={`/admin/campaigns/${campaign.campaignSlug}`}
                            className="rounded-lg border bg-card p-3 transition-colors hover:border-secondary/60">
                            <span className="block font-medium">{campaign.campaignTitle}</span>
                            <span className="text-xs text-muted-foreground">{campaign.role === "GAME_MASTER" ? "Spielleiter" : "Spieler"}</span>
                        </Link>)}
                    </div>
                </section>
            </div>}
        </main>
    </div>;
}

function UserAvatar({user, large = false}: {user: AdminUser; large?: boolean}) {
    const size = large ? "size-16" : "size-9";
    return <span className={`grid shrink-0 place-items-center overflow-hidden rounded-full border bg-background ${size}`}>
        {user.pictureUrl ? <img src={user.pictureUrl} alt="" className="size-full object-cover"/> : <UserRound className={large ? "size-8" : "size-4"}/>}
    </span>;
}

function formatLogin(value?: string | null) {
    return value ? new Date(value).toLocaleString("de-DE") : "Noch nie";
}
